import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { AppId, FCX, usePlatformContext } from "@datassential/platform-ui-lib";
import { requestTrialByFeature } from "@actions/page";
import { ChainsGrowthData } from "@apiHooks/useHomeChainsGrowthData";
import { routeNames } from "@core/routeNames";
import getFormattedThousand from "@core/getFormattedThousand";
import Table from "@components/new/Table/Table";
import { iTableConfigItem } from "@components/new/Table/Table.helpers";
import { RestaurantSegmentEnum } from "@enums";
import { getFormattedThousandWithDecimal, getValueWithSign } from "@helpers";
import { PermissionLevelEnum } from "@models/PermissionSection";
import HomeSectionSubTitle from "@pages/Home/components/HomeSectionSubTitle";
import AccessLink from "@components/AccessLink/AccessLink";
import HomeSectionFooter from "@pages/Home/components/HomeSectionFooter";
import { EMPTY_CELL_VALUE, HOME_TABLE_COL_HIGHLIGHT_STYLE } from "@pages/Home/constants";
import { RESTAURANT_SEGMENT_LABELS } from "../constants";
import HeaderCell from "./HeaderCell";
import "./HomePlacesGrowingChains.styles.scss";

export interface HomePlacesGrowingChainsProps {
    selectedSegmentId: RestaurantSegmentEnum;
    data: ChainsGrowthData;
}

const HEADER_HEIGHT = "auto";
const AVERAGE_ROW_CHAIN_ID = 0;
const MILLION = 10**6;

const HomePlacesGrowingChains: FCX<HomePlacesGrowingChainsProps> = ({ selectedSegmentId, data }) => {
    const dispatch = useDispatch();    
    const { userInfo: { products } } = usePlatformContext();
    const { segments, year } = data.data;

    const chainsProduct = useMemo(() => products[AppId.Chains], [products]);

    const selectedSegmentData = useMemo(
        () => segments.get(selectedSegmentId),
        [segments, selectedSegmentId]
    );

    const hasAccess = useMemo(
        () => data?.permissionLevel === PermissionLevelEnum.Full,
        [data?.permissionLevel]
    );
    
    const requestTrial = useCallback(() => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('chains'));
    }, []);
    
    const topChains = useMemo(() => {
        const chains = selectedSegmentData?.topChains || [];
        const { averageGrowth } = selectedSegmentData || {};
        const shouldIncludeAverage = selectedSegmentId !== RestaurantSegmentEnum.ALL && averageGrowth;

        if (selectedSegmentId === RestaurantSegmentEnum.ALL) {
            const segmentOrder = Object.values(RESTAURANT_SEGMENT_LABELS);
            return [...chains].sort((a, b) =>
                segmentOrder.indexOf(a.chainName ?? '') - segmentOrder.indexOf(b.chainName ?? ''));
        }

        return shouldIncludeAverage ? [...chains, averageGrowth] : chains;
    }, [selectedSegmentData, selectedSegmentId]);

    const config: iTableConfigItem[] = useMemo(() => [
        {
            title: "",
            styles: { cell: { padding: 0 }, head: { height: HEADER_HEIGHT } },
            width: "50%",
            Value: ({ index }) => {
                const chain = topChains[index];

                if (chain?.chainId === AVERAGE_ROW_CHAIN_ID) {
                    return <>{chain?.chainName}</>;
                }
                
                return (
                    <Link
                        to={{ pathname: routeNames.chainDetails, search: `id=${chain?.chainId}` }}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {chain?.chainName}
                    </Link>
                );
            }
        },
        {
            styles: { head: { height: HEADER_HEIGHT } },
            renderTitle: <HeaderCell title="Avg Units" subtitle={year ? String(year) : ''} />,
            textCentered: true,
            Value: ({ index }) => {
                const units = topChains[index]?.units;
                return <>{units ? getFormattedThousand(String(units)) : EMPTY_CELL_VALUE}</>;
            },
        },
        {
            styles: {
                head: { height: HEADER_HEIGHT },
                cell: HOME_TABLE_COL_HIGHLIGHT_STYLE,
            },
            renderTitle: <HeaderCell title="Units Growth" subtitle="1 yr" />,
            textCentered: true,
            Value: ({ index }) => {
                const growth = topChains[index]?.unitsGrowth;
                return <>{growth ? getValueWithSign(Math.round(growth)) : EMPTY_CELL_VALUE}</>;
            },
        },
        {
            styles: { head: { height: HEADER_HEIGHT } },
            renderTitle: <HeaderCell title="Est. Annual Sales" subtitle={year ? `${year}: $mm` : ''} />,
            textCentered: true,
            Value: ({ index }) => {
                const sales = topChains[index]?.estimatedAnnualSales;
                return <>{sales ? getFormattedThousandWithDecimal(sales/MILLION, 1) : EMPTY_CELL_VALUE}</>;
            },
        },
        {
            styles: { head: { height: HEADER_HEIGHT } },
            renderTitle: <HeaderCell title="Sales Growth" subtitle="1 yr" />,
            textCentered: true,
            Value: ({ index }) => {
                const growth = topChains[index]?.salesGrowth;
                return <>{growth ? `${getValueWithSign(Number(growth.toFixed(1)))}%` : EMPTY_CELL_VALUE}</>;
            },
        }
    ], [topChains]);

    const getRowProps = useCallback((index: number) => {
        const isAverageRow = topChains[index]?.chainName === selectedSegmentData?.averageGrowth?.chainName;

        return isAverageRow ? { 'data-average-row': true } : {};
    }, [topChains, selectedSegmentData?.averageGrowth?.chainName]);

    return (
        <div className="HomePlacesGrowingChains">
            <HomeSectionSubTitle>Year Over Year Growth</HomeSectionSubTitle>
            <Table
                className="appears-top"
                key={selectedSegmentId}
                config={config}
                isCopyButtonHidden
                rowsCount={topChains.length}
                hasInnerScroll={false}
                getRowProps={getRowProps}
            />
            <HomeSectionFooter>
                <AccessLink
                    url={chainsProduct?.url || ''}
                    text="More US Chains"
                    hasAccess={hasAccess}
                    onContactUsClick={requestTrial}
                    testId="linkHomePlacesGrowingChainsMoreChains"
                />
            </HomeSectionFooter>
        </div>
    );
}

export default HomePlacesGrowingChains;